import ActiveToggleButton from "@components/ActiveToggleButton";
import { BaseTable } from "@components/BaseTable";
import CustomButton from "@components/common/Button";
import { DropdownService, WorkflowService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { Workflow } from "@/utils/types/workflowMain";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const WorkflowListPage = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Fetch workflows data
  const {
    data: workflows,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["workflows", page, pageSize],
    queryFn: () => WorkflowService.getAllWorkflows({ page, pageSize }),
  });

  const gridData = workflows?.results ?? [];
  const total = workflows?.count ?? 0;

  // Handle delete workflow
  const handleDelete = async (id: string) => {
    setIsDeleting(id);
    const response = await WorkflowService.deleteWorkflow(id);
    if (response) {
      refetch();
    }
    setIsDeleting(null);
  };

  // Handle edit workflow
  const handleEdit = (id: string) => {
    navigate(navigationRoutes.WORKFLOW_EDIT(id));
  };

  // Handle add new workflow
  const handleAdd = () => {
    navigate(navigationRoutes.WORKFLOW_ADD);
  };

  // Check if any action is in progress
  const isAnyActionInProgress = isLoading ?? isDeleting !== null;
  const { data: schemaOptions } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });
  const getSchemaNameById = (id: string) => {
    return schemaOptions?.find(
      (state: { value: string; label: string }) => state.value === id
    );
  };

  // Table columns configuration
  const columns: ProColumns<Workflow>[] = [
    {
      title: "Workflow Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name!.localeCompare(b.name!),
    },
    {
      title: "Schema Name",
      dataIndex: "schema_name",
      key: "schema_name",
      render: (_, entity) => {
        const value = entity.schema_name;
        if (!value) {
          return "Not selected";
        }
        const schema = getSchemaNameById(value);
        return schema?.label ?? "Not selected";
      },
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.workflow}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record.id)}
          type="text"
          title="Edit Workflow"
          disabled={isAnyActionInProgress}
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this workflow?"
          description="This action cannot be undone."
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
          disabled={isAnyActionInProgress}
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Workflow"
            loading={isDeleting === record.id}
            disabled={isAnyActionInProgress}
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <BaseTable<Workflow>
      loading={isLoading}
      columns={columns}
      dataSource={gridData}
      page={page}
      pageSize={pageSize}
      total={total}
      rowKey="id"
      toolBarRender={() => [
        <CustomButton
          key="add"
          type="primary"
          icon={<Plus size={16} />}
          onClick={handleAdd}
          disabled={isAnyActionInProgress}
          title="Add New Workflow"
        >
          Add Workflow
        </CustomButton>,
      ]}
      headerTitle="Workflows List"
      onPageChange={(page, size) => {
        setPage(page);
        setPageSize(size ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", size?.toString() ?? "10");
        navigate(
          `${navigationRoutes.WORKFLOW_LIST}?${searchParams.toString()}`
        );
      }}
      request={() => refetch()}
      tableName={gridName.workflowTable}
    />
  );
};

export default WorkflowListPage;
